import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import Seo from '../components/Seo';
import Faq from '../components/Faq';
import { Reveal, MaskLine, Eyebrow } from '../components/Reveal';
import ParticleField from '../components/ParticleField';
import Marquee from '../components/Marquee';
import { stats, products, img, EMAIL, waLink } from '../data/content';
import { pageSeo, productSeo, homeFaqs, organizationSchema, faqSchema, SITE_URL } from '../data/seo';
import { IS_SSR } from '../lib/env';

const teaserIds = ['rotary-table', 'spm-compact', 'front-load', 'through-feed', 'ultrasonic', 'robotic'];
const teaser = products.filter((p) => teaserIds.includes(p.id));

const chapters = [
  {
    num: '01',
    title: 'Structural Integrity',
    body: 'Every Tekwash machine is built to survive 24/7 plant duty — complete S.S. 304 wetted construction, compact cell-layout footprints and progressive multi-stage filtration that keeps baths clean and running costs low.',
    tags: ['S.S. 304 Construction', '4-Stage Filtration', 'Low Maintenance']
  },
  {
    num: '02',
    title: 'Chemical & Kinetic Fluidics',
    body: 'From 5–20 bar automated spray and immersion flood washing to ultrasonic cavitation and 100–1000 bar robotic deburring — we match the right process, pressure and chemistry to every component geometry.',
    tags: ['Spray 5–20 bar', 'Ultrasonic Cavitation', '100–1000 bar Robotic']
  },
  {
    num: '03',
    title: 'Millipore Verification',
    body: 'Cleanliness is proven, not promised. Our in-house cleanliness testing lab verifies results gravimetrically — down to Millipore values under 2 mg/1000 cm² with particle sizes tracked below 300 µm.',
    tags: ['In-House Testing Lab', 'Millipore < 2 mg', 'Particles < 300 µm']
  }
];

const fadeUp = (delay) => ({ initial: IS_SSR ? false : { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay, duration: 0.8 } });

function useHeroParallax() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const x = useTransform(sx, [-0.5, 0.5], [-14, 14]);
  const y = useTransform(sy, [-0.5, 0.5], [-10, 10]);
  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return { x, y, onMouseMove };
}

function HeroCopy() {
  return (
    <div className="lg:col-span-7">
      <Eyebrow className="mb-6 flex items-center gap-3">
        <span className="inline-block h-[1px] w-10 bg-flame" aria-hidden="true" />
        Industrial Component Cleaning Systems — Pune, India
      </Eyebrow>
      <h1 className="font-display font-extrabold text-paper leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
        <MaskLine delay={0.15}>PRECISION</MaskLine>
        <MaskLine delay={0.3}><span className="text-flame">CLEANING</span></MaskLine>
        <MaskLine delay={0.45}>SYSTEMS.</MaskLine>
      </h1>
      <motion.p {...fadeUp(0.8)} className="mt-7 max-w-xl text-base sm:text-lg text-fog leading-relaxed">
        Tekwash Systems designs and builds industrial cleaning machines — from standard washers to
        fully automatic, high-pressure robotic precision lines. Engineered in Pune, installed across India.
      </motion.p>
      <motion.div {...fadeUp(1)} className="mt-9 flex flex-wrap gap-4">
        <Link to="/products" data-testid="hero-cta-explore-machines" className="bg-flame text-white font-semibold px-8 py-4 clip-notch hover:bg-flamedim transition-colors duration-200">
          Explore Machines
        </Link>
        <a
          href={waLink('Hello Tekwash Systems, I have a component cleaning requirement.')}
          target="_blank" rel="noopener noreferrer"
          data-testid="hero-cta-whatsapp"
          className="border border-steelline text-paper px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:border-flame hover:text-flame transition-colors duration-200 flex items-center"
        >
          WhatsApp Our Engineers
        </a>
      </motion.div>
    </div>
  );
}

function HeroChip({ label, value, className, delay, testid }) {
  return (
    <div className={`absolute px-4 py-3 animate-floaty ${className}`} style={delay ? { animationDelay: delay } : undefined} data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-80">{label}</p>
      <p className="font-display font-bold text-lg">{value}</p>
    </div>
  );
}

function HeroVisual({ x, y }) {
  return (
    <motion.div className="lg:col-span-5 relative" style={{ x, y }}>
      <motion.div
        initial={IS_SSR ? false : { opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="relative"
      >
        <div className="clip-notch border border-steelline/80 bg-surface overflow-hidden">
          <img src={img('spm-hero.jpg')} alt="Tekwash SPM compact high-pressure washer" className="w-full aspect-[4/3] object-cover" />
        </div>
        <HeroChip label="Cycle Time" value="30 sec" className="-top-4 -left-4 bg-card border border-steelline text-paper" testid="hero-chip-cycle" />
        <HeroChip label="Spray Pressure" value="15 bar" className="-bottom-4 -right-3 bg-flame text-white" delay="1.2s" testid="hero-chip-pressure" />
        <HeroChip label="Construction" value="SS 304" className="top-1/3 -right-5 hidden md:block bg-card border border-steelline text-paper" delay="2.4s" />
      </motion.div>
    </motion.div>
  );
}

function Hero() {
  const { x, y, onMouseMove } = useHeroParallax();
  return (
    <section className="relative min-h-screen flex items-center blueprint-grid overflow-hidden pt-24 pb-16" onMouseMove={onMouseMove} data-testid="home-hero">
      <ParticleField />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-transparent to-ink" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center relative z-10 w-full">
        <HeroCopy />
        <HeroVisual x={x} y={y} />
      </div>
      <motion.div initial={IS_SSR ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-fog/60" aria-hidden="true">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <span className="h-10 w-[1px] bg-gradient-to-b from-flame to-transparent" />
      </motion.div>
    </section>
  );
}

function StatsBand() {
  return (
    <section className="border-y border-steelline/60 bg-surface/60" data-testid="home-stats-band">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1} testid={s.testid} className={`py-10 px-6 ${i !== 0 ? 'border-l border-steelline/50' : ''} ${i >= 2 ? 'max-lg:border-t max-lg:border-steelline/50' : ''} ${i === 2 ? 'max-lg:border-l-0' : ''}`}>
            <p className="font-display font-extrabold text-3xl sm:text-4xl text-paper">{s.value}</p>
            <p className="mt-2 text-xs sm:text-sm text-fog">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ProductTeaser() {
  const [active, setActive] = useState(0);
  const p = teaser[active];
  return (
    <section className="py-24 lg:py-32" data-testid="home-product-teaser">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow className="mb-4">The Machine Matrix</Eyebrow>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-paper tracking-tight max-w-xl">
              One platform of cleaning machines, engineered around your component.
            </h2>
            <Link to="/products" data-testid="teaser-link-all-products" className="font-mono text-xs uppercase tracking-[0.2em] text-flame hover:text-flamedim transition-colors whitespace-nowrap">
              View All Machines →
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-12">
          <div className="flex flex-wrap gap-2" data-testid="teaser-tabs">
            {teaser.map((t, i) => (
              <button
                key={t.id}
                onClick={() => setActive(i)}
                data-testid={`teaser-tab-${t.id}`}
                className={`px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] border transition-colors duration-200 ${i === active ? 'border-flame text-flame bg-flame/10' : 'border-steelline text-fog hover:text-paper hover:border-fog'}`}
              >
                {t.name}
              </button>
            ))}
          </div>

          <div className="mt-8 grid lg:grid-cols-2 gap-10 items-center bg-surface/50 border border-steelline/60">
            <div className="overflow-hidden max-lg:border-b max-lg:border-steelline/60">
              <motion.img
                key={p.id}
                src={img(p.images[0])}
                alt={`${productSeo[p.id].h1} — Tekwash Systems, Pune`}
                initial={IS_SSR ? false : { opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="w-full aspect-[4/3] object-cover"
                data-testid="teaser-active-image"
              />
            </div>
            <div className="p-8 lg:p-10">
              <motion.div key={p.id + '-text'} initial={IS_SSR ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <h3 className="font-display font-bold text-2xl text-paper" data-testid="teaser-active-name">{p.name}</h3>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-skyc">{p.tagline}</p>
                <p className="mt-5 text-fog leading-relaxed">{p.desc}</p>
                <ul className="mt-6 space-y-2">
                  {p.bullets.slice(0, 3).map((b) => (
                    <li key={b} className="flex gap-3 text-sm text-fog">
                      <span className="text-flame mt-[3px]" aria-hidden="true">▸</span>{b}
                    </li>
                  ))}
                </ul>
                <Link to={`/products/${productSeo[p.id].slug}`} data-testid="teaser-detail-link" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-flame hover:text-paper transition-colors">
                  View {productSeo[p.id].h1} <span aria-hidden="true">→</span>
                </Link>
              </motion.div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="py-24 lg:py-32 bg-surface/40 border-y border-steelline/60" data-testid="home-manifesto">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow className="mb-4">Why Tekwash</Eyebrow>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-paper tracking-tight max-w-2xl">
            Three disciplines separate a washing machine from a cleaning system.
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-3 gap-px bg-steelline/50 border border-steelline/50">
          {chapters.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.15} className="bg-ink p-8 lg:p-10 hover:bg-card transition-colors duration-300" testid={`manifesto-chapter-${c.num}`}>
              <p className="font-display font-extrabold text-6xl text-outline">{c.num}</p>
              <h3 className="mt-6 font-display font-bold text-xl text-paper">{c.title}</h3>
              <p className="mt-4 text-sm text-fog leading-relaxed">{c.body}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {c.tags.map((t) => (
                  <span key={t} className="font-mono text-[10px] uppercase tracking-[0.15em] text-skyc border border-steelline px-3 py-1.5">{t}</span>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceStrip() {
  return (
    <section className="py-24 lg:py-32" data-testid="home-service">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Eyebrow className="mb-4">After-Sales Service Support</Eyebrow>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-paper tracking-tight">
            Service so prompt, every OEM order is a repeat order.
          </h2>
          <p className="mt-6 text-fog leading-relaxed">
            Continuous improvement in prompt after-sales service has resulted in repeat orders from all OEMs
            across India. Service support is our main focus — monitored directly by our Directors.
          </p>
          <ul className="mt-8 space-y-4">
            {[
              '3 skilled & experienced service engineers based at Pune',
              'Adequate stock of spare-wear parts at our office',
              'Maintenance & fault training delivered by our installation team'
            ].map((point) => (
              <li key={point} className="flex gap-4 items-start">
                <span className="mt-1.5 h-2 w-2 bg-flame shrink-0" aria-hidden="true" />
                <span className="text-paper/90">{point}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="clip-notch border border-steelline/80 overflow-hidden">
            <img src={img('facility.jpg')} alt="Tekwash production facility, Pune" className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="relative border-t border-steelline/60 blueprint-grid" data-testid="home-cta">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32 text-center relative z-10">
        <Reveal>
          <Eyebrow className="mb-6">Start The Conversation</Eyebrow>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-paper tracking-tight max-w-3xl mx-auto">
            Let's spec your cleaning line.
          </h2>
          <p className="mt-6 text-fog max-w-xl mx-auto">Send us your component, its geometry and your cleanliness target — our engineers will respond with the right machine and process.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a href={`mailto:${EMAIL}?subject=Component%20Cleaning%20Requirement`} data-testid="cta-email-button" className="bg-flame text-white font-semibold px-8 py-4 clip-notch hover:bg-flamedim transition-colors duration-200">
              Email {EMAIL}
            </a>
            <a
              href={waLink('Hello Tekwash Systems, I have a component cleaning requirement.')}
              target="_blank" rel="noopener noreferrer"
              data-testid="cta-whatsapp-button"
              className="border border-steelline text-paper px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:border-flame hover:text-flame transition-colors duration-200 flex items-center"
            >
              WhatsApp +91 9960816769
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const schemas = [
    organizationSchema,
    { '@context': 'https://schema.org', '@type': 'WebSite', name: 'Tekwash Systems Pvt Ltd', url: SITE_URL, publisher: { '@id': `${SITE_URL}/#organization` } },
    faqSchema(homeFaqs)
  ];
  return (
    <>
      <Seo {...pageSeo.home} schemas={schemas} />
      <Hero />
      <StatsBand />
      <Marquee />
      <ProductTeaser />
      <Manifesto />
      <ServiceStrip />
      <Faq faqs={homeFaqs} heading="Component cleaning machines — answers for plant & purchase teams" testid="home-faq" />
      <CTA />
    </>
  );
}
